import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';

export type AssetType = 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';

interface OptimizedConversation {
  id: string;
  listing_id: string;
  seller_id: string;
  buyer_id: string;
  subject: string;
  status: string;
  last_message_at: string;
  created_at: string;
  updated_at: string;
  asset_type?: string;
  listing?: {
    title: string;
    price_usd?: number | null;
    image?: string | null;
  };
  other_user?: {
    display_name: string;
    avatar_url: string | null;
  };
  unread_count?: number;
}

/**
 * Ultra-optimized conversations hook using v_conversation_summaries database view.
 * 
 * PERFORMANCE:
 * - Before: N*3 queries (1 per conversation for profiles, listings, messages)
 * - Intermediate: 4 batch queries (profiles, listings, messages in parallel)
 * - Now: 1-2 queries total using pre-computed database view
 * 
 * The v_conversation_summaries view pre-computes:
 * - Unread message counts per user
 * - Total message count
 * - Listing title and price from ALL asset types (helicopter, engine, parts, jobs, services)
 * - Listing hero image
 */
export const useOptimizedConversations = () => {
  const [conversations, setConversations] = useState<OptimizedConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch conversations using the optimized database view - 1-2 queries total
  const fetchConversations = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Query 1: Get all conversation data from the optimized view
      // This single query returns pre-computed unread counts and listing details
      const { data: viewData, error: viewError } = await supabase
        .from('v_conversation_summaries')
        .select('*')
        .or(`seller_id.eq.${user.id},buyer_id.eq.${user.id}`)
        .order('last_message_at', { ascending: false })
        .limit(100);

      if (viewError) throw viewError;

      if (!viewData || viewData.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
      }

      // Query 2: Batch fetch profiles for other users
      // (View doesn't include profile data to keep it lightweight)
      const otherUserIds = [...new Set(viewData.map(c => 
        c.seller_id === user.id ? c.buyer_id : c.seller_id
      ))];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', otherUserIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      // Filter to only conversations with messages and transform
      const conversationsWithMessages = viewData.filter(c => (c.total_messages || 0) > 0);

      const transformed: OptimizedConversation[] = conversationsWithMessages.map(conv => {
        const isSeller = conv.seller_id === user.id;
        const otherUserId = isSeller ? conv.buyer_id : conv.seller_id;
        const profile = profileMap.get(otherUserId);

        // Use pre-computed unread count from view
        const unreadCount = isSeller 
          ? (conv.seller_unread_count || 0)
          : (conv.buyer_unread_count || 0);

        return {
          id: conv.id,
          listing_id: conv.listing_id,
          seller_id: conv.seller_id,
          buyer_id: conv.buyer_id,
          subject: conv.subject,
          status: conv.status,
          last_message_at: conv.last_message_at,
          created_at: conv.created_at,
          updated_at: conv.updated_at,
          asset_type: conv.asset_type || undefined,
          listing: {
            title: conv.listing_title || 'Unknown Listing',
            price_usd: conv.listing_price,
            image: conv.listing_image
          },
          other_user: profile 
            ? {
                display_name: profile.display_name || 'Unknown User',
                avatar_url: profile.avatar_url
              }
            : { display_name: 'Unknown User', avatar_url: null },
          unread_count: unreadCount
        };
      });

      setConversations(transformed);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching conversations:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to load conversations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  // Create or get existing conversation
  const createOrGetConversation = async (
    listingId: string,
    sellerId: string,
    subject: string,
    assetType: AssetType = 'helicopter'
  ) => {
    if (!user) throw new Error('User not authenticated');

    // Prevent self-conversations
    if (sellerId === user.id) {
      toast({
        title: 'Cannot message yourself',
        description: 'You cannot start a conversation on your own listing.',
        variant: 'destructive',
      });
      throw new Error('Cannot message yourself');
    }

    try {
      // First check if conversation already exists
      const { data: existing } = await supabase
        .from('conversations')
        .select('id')
        .eq('listing_id', listingId)
        .eq('seller_id', sellerId)
        .eq('buyer_id', user.id)
        .maybeSingle();

      if (existing) {
        return existing.id;
      }

      // Create new conversation with asset_type
      const { data, error } = await supabase
        .from('conversations')
        .insert({
          listing_id: listingId,
          seller_id: sellerId,
          buyer_id: user.id,
          subject,
          asset_type: assetType
        })
        .select('id')
        .single();

      if (error) throw error;
      
      await fetchConversations(); // Refresh conversations
      return data.id;
    } catch (error: any) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  };

  // Mark conversation as read
  const markConversationAsRead = async (conversationId: string) => {
    if (!user) return;

    try {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', user.id);

      // Update local state immediately for instant feedback
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, unread_count: 0 }
            : conv
        )
      );
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  // Real-time subscription with user-scoped filtering for better performance
  useEffect(() => {
    if (!user) return;

    fetchConversations();

    // Subscribe to conversation changes - filter to user's conversations only
    const conversationsChannel = supabase
      .channel('optimized-conversations-v2')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `seller_id=eq.${user.id}`
        },
        () => fetchConversations()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `buyer_id=eq.${user.id}`
        },
        () => fetchConversations()
      )
      .subscribe();

    // Subscribe to message changes - uses debounced refetch
    let messageRefetchTimeout: NodeJS.Timeout | null = null;
    const debouncedRefetch = () => {
      if (messageRefetchTimeout) clearTimeout(messageRefetchTimeout);
      messageRefetchTimeout = setTimeout(() => fetchConversations(), 300);
    };

    const messagesChannel = supabase
      .channel('optimized-messages-v2')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        debouncedRefetch
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        },
        debouncedRefetch
      )
      .subscribe();

    return () => {
      if (messageRefetchTimeout) clearTimeout(messageRefetchTimeout);
      supabase.removeChannel(conversationsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [user?.id, fetchConversations]);

  return {
    conversations,
    loading,
    error,
    createOrGetConversation,
    markConversationAsRead,
    refetch: fetchConversations
  };
};

export default useOptimizedConversations;
